<template>
  <div id="schedulePage" class="page white" refs="schedulePage">
    <el-container class="content-aside">
      <el-main class="cpl-main">
        <div class="options">
          <el-row :gutter="20">
            <!-- Schedule Search -->
            <el-col :span="12" class="search-col">
              <div :class="{'player-search': true, 'hidden': selectedSchedule !== null}">
                <schedule-search-bar
                  ref="scheduleSearchBar"
                  :tagsProp="selectedTagsFromSlider"
                  :presentPlayerListProp="scheduleList"
                  :presentPlayerListByBatchProp="scheduleList.batchHistory"
                  :triggerPreviousSearchProp="triggerPreviousSearch"
                  :selectedDayProp="selectedDay"
                  @schedule-search-bar-change="handleScheduleSearchBarChange($event)">
                </schedule-search-bar>
              </div>
            </el-col>
          </el-row>
        </div>

        <tag-slider v-if="!selectedSchedule" :class="{'hidden': selectedSchedule !== null}"
          :accessorComponentProp="'schedulePagePlayers'"
          :refreshTagSliderProp="refreshTagSlider"
          @tag-slider-change="handleTagSliderChange($event)">
        </tag-slider>

        <selected-schedule-tag-slider v-if="selectedSchedule"
          :tagsProp="selectedScheduleTags"
          @selected-schedule-tags-slider-change="handleSelectedScheduleTagsSliderChange($event)">
        </selected-schedule-tag-slider>

        <!-- Scheduled Players -->
        <el-row :gutter="20" :class="['scheduled-player-row main-content-row', {'has-item-selected': selectedSchedule !== null}]">
          <div id="presentation" :class="{'loaded': scheduleListLoaded && tagsSliderLoaded}">
            <div class="content">
              <!--Time Slider-->
              <div class="time-slider-control">
                <div id="timeSliderControl-background" class="time-slider-control-content hide-scrollbar"></div>
                <perfect-scrollbar @vue:mounted="perfectScrollbarMounted = true" id="timeSliderControl" ref="timeSliderControl" class="cstm-ps-scrollbar-horizontal show-ps-rail-x-default time-slider-control-content">
                  <div class="times-item-content"></div>
                </perfect-scrollbar>
              </div>

              <!-- Time Label -->
              <div class="time-label">
                <div class="time-label-content time-slider-control-sync">
                  <div class="times-item-content">
                    <div v-for="(time, indx) in playerHoursList" :key="indx" class="time-item">
                      <div class="time-item-content">
                        <div class="hour">{{time.H}}</div>
                        <div class="half">{{time.halfH}}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <BaseEmptyState
                v-if="scheduleListLoaded && scheduleList.itemsToShow.length < 1 && !isSearchLoading"
                class="py-10"
                data-component-element-id="empty-schedule-list-placeholder"
              >
                <template #default>
                  <div class="text-sm text-gray-600">
                    {{ showNoSearchResults ? $t('texts.emptyFilterTablePlaceholder') : $t('texts.noContentHasBeenScheduledYet') }}
                  </div>
                </template>
              </BaseEmptyState>

              <!-- List or Schedules -->
              <div
                v-else
                id="schedulePlayerListContainer"
                class="schedules schedule-player-list hide-scrollbar"
              >
                <div v-for="(schedule, indx) in scheduleList.itemsToShow" :key="indx"
                  :id="`scheduleRow-${schedule._edit.uid}`"
                  :class="['schedule-row-item', {'row-media-action-selected': schedule._edit.isRowMediaActionSelected,
                  'vertical': schedule.dir === 'vertical' || schedule._edit.ledVertical,
                  'led': schedule._edit.led,
                  'no-events': (schedule.times && schedule.times.length < 1),
                  'mouse-over': mouseOverScheduleRow === schedule._edit.uid,
                  'mouse-out': mouseOverScheduleRow !== schedule._edit.uid}]"
                  :style="{height: `${schedule._edit.scheduleRowHeight}px`}"
                  @mouseover="handleScheduleItemMouseEvent('mouseover', schedule, $event)"
                  @mouseout="handleScheduleItemMouseEvent('mouseout', schedule, $event)"
                  @click.capture="handleScheduleItemClickCapture($event, schedule)">

                  <div class="schedule-preview">
                    <div v-if="!selectedSchedule" class="preview-card-wrapper grey-background" :style="schedule._edit.previewCardWrapperStyle">
                      <div :class="['preview-card']" :style="schedule._edit.previewCardStyle">
                        <div class="media-preview-fixed">
                          <template v-if="schedule.hasOwnProperty('preview') && schedule.preview !== null">
                            <!-- Image Preview -->
                            <img v-if="schedule.preview.type === 'img'" :src="schedule.preview.url" class="object-fit_cover_center">

                            <!-- Project Preview -->
                            <template v-if="['svg', 'html'].indexOf(schedule.preview.type) >= 0">
                              <iframe :id="`scheduleListPreview-${schedule.scheduleId}`"
                                :src="$utils.filterMediaPreviewUrl(schedule.preview.url, { queryParams: { dimensions: schedule.dimensions } })"
                                :data-src="$utils.trimDataSrc(schedule.preview.url, { queryParams: { dimensions: schedule.dimensions } })"
                                @load="handleSVGOnLoad({ schedule })"
                                :class="['object-fit_cover_center', 'template-target-on-refresh-token', `media-preview-${schedule._edit.uid}`]">
                              </iframe>
                            </template>
                          </template>

                          <!-- No Preview -->
                          <template v-if="!schedule.hasOwnProperty('preview') || schedule.preview === null">
                            <div class="no-preview">
                              <div class="np-content">
                                <el-icon class="icon"><PictureFilled /></el-icon>
                                {{$t('label.previewUnavailable')}}
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>

                      <div class="preview-card-wrapper-cover" :style="schedule._edit.previewCardStyle"></div>
                      <div v-if="schedule.dir === 'vertical'" class="vertical-left-preview-card-wrapper deny-row-click-capture"></div>
                    </div>
                  </div>

                  <div class="schedule-content">
                    <!-- Row content background -->
                    <div v-if="schedule._isInErrorStatus" class="background-error-status">
                      <div :class="schedule._edit.backgroundClassName" class="esg-background"></div>
                    </div>
                    <div v-else :class="schedule._edit.backgroundClassName" class="background-ok-status"></div>

                    <!-- Schedule Name -->
                    <div class="schedule-name">
                      {{schedule.name}}
                      <template v-if="schedule._isInErrorStatus && schedule._playerStatus === 'disconnected'">
                        {{' '}} <span class="player-status">{{$t('label.offline')}}</span>
                      </template>
                      <template v-else-if="schedule._isInErrorStatus && schedule._playerStatus === 'unconfigured'">
                        {{' '}} <span class="player-status">{{$t('label.notConfigured')}}</span>
                      </template>

                      <div v-if="schedule._isInErrorStatus && schedule._playerStatus === 'unconfigured'"
                        class="setup-not-configured-player"> {{$t('label.setup')}}
                      </div>
                    </div>

                    <!--Timeline-->
                    <div class="times time-slider-control-sync">
                      <div class="times-content">
                        <div v-for="(time, indx) in schedule.times" :key="indx"
                          :style="time._edit.style" :class="['time-item', {'priority-time': time._edit.showPriorityIcon}]">
                          <div class="priority-icon"></div>
                          {{time._edit.textLabel}}
                        </div>
                      </div>
                    </div>

                    <!-- Total Durations -->
                    <div v-if="schedule.mediaDurations" class="schedule-media-durations">
                      <el-tag
                        v-if="schedule.mediaDurations.adds"
                        :key="`media-total-durations-adds-${0}`"
                        :closable="false"
                        :disable-transitions="true"
                        :style="{ backgroundColor: '#36425c' }"
                        :class="['media-total-durations-adds']">
                        <div class="duration-tag-val"><span>{{$utils.formatDurationToMinutesAndSeconds({duration: schedule.mediaDurations.adds})}}</span></div>
                      </el-tag>

                      <el-tag
                        v-if="schedule.mediaDurations.content"
                        :key="`media-total-durations-content-${0}`"
                        :closable="false"
                        :disable-transitions="true"
                        :style="{'backgroundColor': 'rgba(255, 0, 0, 0)'}"
                        :class="['media-total-durations-content']">
                        <div class="duration-tag-val"><span>{{$utils.formatDurationToMinutesAndSeconds({duration: schedule.mediaDurations.content})}}</span></div>
                      </el-tag>
                    </div>

                    <!-- Row actions -->
                    <div v-if="enableEditCalendar" class="media-actions">
                      <!-- Delete Media -->
                      <el-button :class="{'cstm-icon-btn-medium remove media-action-icon deleteMediaSchedule': true}"></el-button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Schedule Item Full details -->
              <div id="schedule-item-full-details-wrapper" class="grey-background" v-if="selectedSchedule !== null">
                <selected-schedule-item
                  ref="scheduleItemFullDetailsComp"
                  :canvasRepository="canvasRepository"
                  :scheduleItemProp="selectedSchedule"
                  :selectedDayProp="selectedDay"
                  :parentComponentProp="thisComponentAsReferenceToAnyChildComponents"
                  :triggerPropertyActionResponseFromMediaMenuProp="triggerPropertyActionResponseFromMediaMenu"
                  :updatedMediaEventsProp="updatedMediaEvents"
                  :selectedScheduleTagsProp="selectedScheduleTags"
                  @schedule-item-change="handleScheduleItemCompChange($event)">
                </selected-schedule-item>
              </div>
            </div>
          </div>
        </el-row>
      </el-main>

      <!-- Right Side -->
      <el-aside class="cpl-aside cpl-asside-schedule-page">
        <div class="content schedule-menu-content right-side-menu hide-scrollbar">
          <!-- Monthly Calendar -->
          <div id="schedCalendarWrapper" :class="{'is-visible': playtimeFormData === null}">
            <div class="schedule-calendar-content">
              <monthly-calendar
                class="cal-current-month"
                :monthlyCalendarTypeProp="'currentMonth'"
                :selectedDayProp="selectedDay"
                :isVisibleProp="true"
                :key="'currentMonthKey'"
                @monthly-calendar-change="handleMonthlyCalendarChange($event)">
              </monthly-calendar>

              <monthly-calendar
                class="cal-next-month"
                :monthlyCalendarTypeProp="'nextMonth'"
                :selectedDayProp="selectedDay"
                :isVisibleProp="true"
                :key="'nextMonth'"
                @monthly-calendar-change="handleMonthlyCalendarChange($event)">
              </monthly-calendar>
            </div>
          </div>

          <template v-if="playtimeFormData !== null">
            <div class="playtime-form-wrapper">
              <div class="playtime-form-content">
                <div class="playtime-title-text">{{$t('label.playTime')}}</div>

                <div class="playtime-title">
                  <el-tag :class="[{'selected': showPlaytimeForm}]">
                    <div class="background-color"></div>
                    <div @click="handlePlaytimeFormTagClick" class="tag-val">
                      {{playtimeFormData._edit.title}}
                    </div>
                  </el-tag>
                </div>

                <template v-if="showPlaytimeForm">
                  <div class="form-title"></div>
                  <playtime-form ref="newPlaytimeForm"
                    :playtimeProp="playtimeFormData"
                    :parentAccessorComponentProp="'schedulePage'"
                    :mediaSelectionProp="(mediaEventsSelected && mediaEventsSelected.mediaSelection) ? mediaEventsSelected.mediaSelection : []"
                    @playtime-form-change="handlePlaytimeFormChange($event)">

                    <!-- Playtime tags summary -->
                    <div v-if="playtimeTags !== null && playtimeTags.length" class="player-tags-wrapper">
                      <div class="player-tags-title">{{$t('label.player', 2)}}</div>
                      <div class="tags-wrapper">
                        <el-tag v-for="(tag, indx) in playtimeTags" :key="indx"
                          :closable="false"
                          :disable-transitions="true"
                          :style="{'backgroundColor': (tag.backgroundColor ? tag.backgroundColor: '')}"
                          :class="[{'with-adds-exclusion': showTagExcludeIcon.show}]">
                          <div class="tag-val">
                            <span>{{tag.label}}</span>
                            <div v-if="showTagExcludeIcon && showTagExcludeIcon.show"
                              @click="handleTagExcludeIconClick(tag.value)"
                              :class="['adds-exclusion', {'exclude': (showTagExcludeIcon.icon === 'exclude')}]">
                            </div>
                          </div>
                        </el-tag>
                      </div>
                    </div>

                    <el-button @click="handleSubmitPlaytimeForm" v-if="showPlaytimeFormSubmitBtn" class="cstm-btn-gradient width-125 sbmt-playtime-form-btn">{{$t('label.ok')}}</el-button>
                  </playtime-form>
                </template>
              </div>
            </div>
          </template>
        </div>

        <loader v-if="showSideLoader" :isRequesting="true" :textLabel="''"></loader>
      </el-aside>
    </el-container>

    <div
      v-if="notification"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
    >
      <BaseNotificationAlertsItem
        :notifId="notification.notifId"
        :type="notification.type"
        :icon="notification.icon"
        :htmlTitle="notification.htmlTitle"
        :htmlMessage="notification.htmlMessage"
        :requestId="notification.requestId"
        :closable="notification.closable"
        @dismiss="handleDismissNotification"
      />
    </div>
  </div>
</template>

<script src="./schedule.js"></script>
<style lang="scss" src="./schedule.scss"></style>
